import '../styles/GlobalDesign.css';
import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || '/api');

const emptyConfig = {
  enabled: false,
  provider: 'BunnyCDN',
  cdnUrl: '',
  directPublicUrl: '',
  segmentPublicUrl: '',
  segmentPublicUrls: [],
  pullZoneId: '',
  profiles: [],
  activeCdnCount: 0,
  apiKeyConfigured: false,
  source: '',
  updatedAt: '',
};

const getToken = () => localStorage.getItem('token') || localStorage.getItem('adminToken');

function AdminLiveCdn() {
  const [config, setConfig] = useState(emptyConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const headers = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getToken()}`,
  });

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/admin/streaming/cdn-live`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Impossible de charger la configuration CDN');
      }
      setConfig({ ...emptyConfig, ...data.config });
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async (override = {}) => {
    setSaving(true);
    setError('');
    setMessage('');
    setTestResult(null);
    const nextConfig = { ...config, ...override };

    try {
      const res = await fetch(`${API_URL}/admin/streaming/cdn-live`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify(nextConfig),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Erreur lors de la sauvegarde');
      }
      setConfig({ ...emptyConfig, ...data.config });
      setMessage(data.message || 'Configuration CDN live sauvegardée');
    } catch (err) {
      setError(err.message || 'Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  const testConfig = async () => {
    setTesting(true);
    setError('');
    setMessage('');
    setTestResult(null);

    try {
      const res = await fetch(`${API_URL}/admin/streaming/cdn-live/test`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(config),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Erreur pendant le test');
      }
      setTestResult(data.test);
    } catch (err) {
      setError(err.message || 'Erreur réseau');
    } finally {
      setTesting(false);
    }
  };

  const setField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const setProfileField = (profileId, field, value) => {
    setConfig(prev => ({
      ...prev,
      profiles: (prev.profiles || []).map(profile =>
        profile.id === profileId ? { ...profile, [field]: value } : profile
      )
    }));
  };

  const toggleProfile = (profileId) => {
    const profiles = (config.profiles || []).map(profile =>
      profile.id === profileId ? { ...profile, enabled: !profile.enabled } : profile
    );
    saveConfig({ profiles });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-page">
          <div className="admin-loading">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  const profiles = config.profiles || [];
  const activeCount = profiles.filter(profile => profile.enabled).length;
  const currentStatus = activeCount > 0 ? `${activeCount} actif${activeCount > 1 ? 's' : ''}` : 'Désactivé';
  const segmentUrls = config.segmentPublicUrls?.length ? config.segmentPublicUrls : [config.segmentPublicUrl].filter(Boolean);

  return (
    <AdminLayout>
      <div className="admin-page">
        <div className="admin-page-header">
          <h1 className="admin-page-title">CDN Live HLS</h1>
          <p className="admin-page-subtitle">Activation BunnyCDN pour les segments live uniquement.</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        <div className="admin-card">
          <div className="live-cdn-header">
            <div>
              <h2 className="card-title">Etat CDN</h2>
              <div className="live-cdn-status-row">
                <span className={`status-badge ${activeCount > 0 ? 'active' : 'disabled'}`}>{currentStatus}</span>
                <span className="usage-badge">Provider : BunnyCDN</span>
              </div>
            </div>
          </div>

          <div className="live-cdn-grid">
            <div className="form-group live-cdn-full">
              <label className="form-label">URL directe Fluxivo si aucun CDN n'est actif</label>
              <input
                className="form-input"
                type="url"
                placeholder="http://51.254.135.114"
                value={config.directPublicUrl}
                onChange={e => setField('directPublicUrl', e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 18 }}>
            {profiles.map(profile => (
              <div key={profile.id} style={{ border: '1px solid #2a2f45', borderRadius: 8, padding: 16, background: '#111827' }}>
                <div className="live-cdn-header" style={{ marginBottom: 12 }}>
                  <div>
                    <h3 className="card-title" style={{ fontSize: 16, marginBottom: 8 }}>{profile.label}</h3>
                    <div className="live-cdn-status-row">
                      <span className={`status-badge ${profile.enabled ? 'active' : 'disabled'}`}>{profile.enabled ? 'Actif' : 'Désactivé'}</span>
                      <span className="usage-badge">Zone : {profile.pullZoneId || 'Non définie'}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className={`btn ${profile.enabled ? 'btn-danger' : 'btn-primary'}`}
                    onClick={() => toggleProfile(profile.id)}
                    disabled={saving}
                  >
                    {saving ? 'Sauvegarde...' : (profile.enabled ? 'Désactiver' : 'Activer')}
                  </button>
                </div>

                <div className="form-group">
                  <label className="form-label">Nom</label>
                  <input
                    className="form-input"
                    type="text"
                    value={profile.label || ''}
                    onChange={e => setProfileField(profile.id, 'label', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">URL BunnyCDN</label>
                  <input
                    className="form-input"
                    type="url"
                    value={profile.cdnUrl || ''}
                    onChange={e => setProfileField(profile.id, 'cdnUrl', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Origine BunnyCDN</label>
                  <input
                    className="form-input"
                    type="url"
                    value={profile.originUrl || ''}
                    onChange={e => setProfileField(profile.id, 'originUrl', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Pull Zone ID</label>
                  <input
                    className="form-input"
                    type="text"
                    value={profile.pullZoneId || ''}
                    onChange={e => setProfileField(profile.id, 'pullZoneId', e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="live-cdn-current">
            <span>URL segments actives</span>
            <code>{segmentUrls.length ? segmentUrls.join(' | ') : '-'}</code>
          </div>

          <div className="live-cdn-meta">
            <span className={`status-badge ${config.apiKeyConfigured ? 'active' : 'pending'}`}>
              API Bunny : {config.apiKeyConfigured ? 'configurée' : 'non configurée'}
            </span>
            <span className="usage-badge">Source : {config.source || 'runtime'}</span>
            {config.updatedAt && <span className="usage-badge">Mis à jour : {config.updatedAt}</span>}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={testConfig} disabled={testing}>
              {testing ? 'Test...' : 'Tester'}
            </button>
            <button type="button" className="btn btn-primary" onClick={() => saveConfig()} disabled={saving}>
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>

        {testResult && (
          <div className="admin-card" style={{ marginTop: '1.5rem' }}>
            <h2 className="card-title">Test de connexion</h2>
            <div className="live-cdn-test-grid">
              <TestBox title="Fluxivo direct" result={testResult.direct} />
              {(testResult.profileTests || []).map(profileTest => (
                <ProfileTestBox key={profileTest.id || profileTest.label} profileTest={profileTest} />
              ))}
            </div>
            <div className={`alert ${testResult.healthy ? 'alert-success' : 'alert-error'}`} style={{ marginTop: '1rem', marginBottom: 0 }}>
              {testResult.healthy ? 'Configuration joignable' : 'Un des accès ne répond pas correctement'}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function TestBox({ title, result = {} }) {
  const ok = Boolean(result.ok);
  return (
    <div className={`live-cdn-test-box ${ok ? 'ok' : 'ko'}`}>
      <div className="live-cdn-test-title">{title}</div>
      <div className={`status-badge ${ok ? 'active' : 'expired'}`}>{ok ? 'OK' : 'Erreur'}</div>
      <div className="live-cdn-test-detail">HTTP : {result.statusCode || '-'}</div>
      <div className="live-cdn-test-detail">Temps : {result.responseTimeMs || '-'} ms</div>
      {result.error && <div className="live-cdn-test-error">{result.error}</div>}
    </div>
  );
}

function ProfileTestBox({ profileTest = {} }) {
  const cdnOk = Boolean(profileTest.cdn?.ok);
  const originOk = profileTest.origin ? Boolean(profileTest.origin.ok) : true;
  const ok = cdnOk && originOk;

  return (
    <div className={`live-cdn-test-box ${ok ? 'ok' : 'ko'}`}>
      <div className="live-cdn-test-title">{profileTest.label || 'BunnyCDN'}</div>
      <div className={`status-badge ${ok ? 'active' : 'expired'}`}>{ok ? 'OK' : 'Erreur'}</div>
      <div className="live-cdn-test-detail">CDN HTTP : {profileTest.cdn?.statusCode || '-'}</div>
      <div className="live-cdn-test-detail">CDN temps : {profileTest.cdn?.responseTimeMs || '-'} ms</div>
      {profileTest.origin && (
        <div className="live-cdn-test-detail">Origine HTTP : {profileTest.origin.statusCode || '-'}</div>
      )}
      {profileTest.cdn?.error && <div className="live-cdn-test-error">{profileTest.cdn.error}</div>}
      {profileTest.origin?.error && <div className="live-cdn-test-error">{profileTest.origin.error}</div>}
    </div>
  );
}

export default AdminLiveCdn;
